import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContentMasterBulkReviewFieldView } from '../api/generated/models/ContentMasterBulkReviewFieldView';
import type { ContentMasterBulkReviewItemView } from '../api/generated/models/ContentMasterBulkReviewItemView';
import { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';
import { ContentMasterProductThumb } from '../components/content-master/ContentMasterProductThumb';
import ContentMasterProBadge from '../components/content-master/ContentMasterProBadge';
import ContentMasterReviewFooter from '../components/content-master/ContentMasterReviewFooter';
import ContentMasterQuotaExceededModal from '../components/content-master/ContentMasterQuotaExceededModal';
import ContentMasterApplyWarningModal, {
  isApplyWarningDismissed,
} from '../components/content-master/ContentMasterApplyWarningModal';
import {
  ContentMasterEyeIcon,
  HTML_PREVIEW_CORE_FIELDS,
  useHtmlPreview,
} from '../components/content-master/HtmlPreview';
import EmptyState from '../components/EmptyState';
import Pagination from '../components/Pagination';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useContentMasterEntitlements } from '../hooks/useContentMasterEntitlements';
import { usePermissions } from '../hooks/usePermissions';
import {
  type ContentMasterBulkReviewTab,
  fetchContentMasterBulkInProgressProducts,
  fetchContentMasterBulkReviewItems,
  markContentMasterProductForEdit,
  setContentMasterBulkReviewFilters,
  setContentMasterSubscriptionInsufficientModalVisible,
  submitContentMasterBulkApply,
  submitContentMasterBulkRetry,
  submitContentMasterProductsReject,
} from '../store/slices/contentMasterSlice';
import { ContentMasterBulkReviewFieldView as ContentMasterBulkReviewFieldViewNs } from '../api/generated/models/ContentMasterBulkReviewFieldView';
import { addNotification } from '../store/slices/notificationSlice';
import {
  contentMasterStatusLabelKey,
  getContentMasterStatusClassName,
} from '../utils/contentMasterProcessingStatus';
import { buildContentMasterChannelOptions } from '../utils/contentMasterWorkspace';

type TabDef = {
  key: ContentMasterBulkReviewTab;
  labelKey: string;
  /** Statuses included in this tab when reading from `state.menuCounts`. */
  countStatuses: ContentMasterProductView.processingStatus[];
};

const TABS: TabDef[] = [
  {
    key: 'generating',
    labelKey: 'content_master_bulk_review_tab_generating',
    countStatuses: [
      ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED,
      ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS,
    ],
  },
  {
    key: 'applying',
    labelKey: 'content_master_bulk_review_tab_applying',
    countStatuses: [
      ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED,
      ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS,
    ],
  },
  {
    key: 'ready',
    labelKey: 'content_master_bulk_review_tab_ready',
    countStatuses: [ContentMasterProductView.processingStatus.BULK_GENERATE_DONE],
  },
  {
    key: 'errors',
    labelKey: 'content_master_bulk_review_tab_errors',
    countStatuses: [
      ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED,
      ContentMasterProductView.processingStatus.BULK_APPLY_FAILED,
    ],
  },
];

const BULK_REVIEW_BANNER_DISMISS_KEY = 'cm_bulk_review_banner_dismissed';

function readBulkReviewBannerDismissed(): boolean {
  try {
    return localStorage.getItem(BULK_REVIEW_BANNER_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Statuses sent to `GET /v2/api/content-master/bulk/review#processingStatus`
 * for the Ready and Errors tabs (as comma-separated strings). The endpoint
 * whitelists exactly these values; everything else is rejected as
 * `content_master_invalid_request`.
 */
const READY_STATUS = ContentMasterProductView.processingStatus.BULK_GENERATE_DONE;
const ERRORS_STATUSES = [
  ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED,
  ContentMasterProductView.processingStatus.BULK_APPLY_FAILED,
].join(',');

const GENERATING_STATUSES = [
  ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED,
  ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS,
].join(',');

const APPLYING_STATUSES = [
  ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED,
  ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS,
].join(',');

/**
 * i18n keys for the 6 core fields. Mirrors the legacy `FIELD_LABELS`
 * mapping in `ContentMasterProductPage.tsx`. Keep them in sync.
 */
const CORE_FIELD_LABEL_I18N: Record<string, string> = {
  title: 'title',
  shortDescription: 'shortDescription',
  description: 'description',
  metaTitleTag: 'titleTag',
  metaKeywords: 'content_master_meta_keywords',
  metaDescription: 'content_master_meta_description',
  urlPath: 'urlPath',
};

function sortStorageLanguageCodes(codes: string[]): string[] {
  return [...codes].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function reviewLanguagesWithDrafts(
  item: ContentMasterBulkReviewItemView
): Record<string, ContentMasterBulkReviewFieldView[]> {
  const src = item.languages ?? {};
  const out: Record<string, ContentMasterBulkReviewFieldView[]> = {};
  for (const code of Object.keys(src)) {
    const fields = src[code]?.fields ?? [];
    if (fields.length > 0) {
      out[code] = fields;
    }
  }
  return out;
}

/**
 * ms - kept in sync with the CSS transition duration on
 * `.content-master-bulk-card` (height/opacity collapse). After the animation
 * finishes we dispatch the actual `markForEdit` thunk; the slice removes the
 * item from `bulkReview.*Items`, React unmounts the card, and `total` /
 * counts update - all in one render commit.
 */
const SEND_TO_EDITOR_ANIM_MS = 320;

/** Common product-row shape consumed by the renderer below. */
interface RenderRow {
  contentMasterProductId: string | null;
  wawiArtikelId: number | null;
  sku: string | null;
  gtin: string | null;
  title: string | null;
  thumbUrl: string | null;
  status: ContentMasterProductView.processingStatus | null;
  /**
   * Operation of the last bulk submit (`/bulk/review` payloads only) - lets
   * the status pill tell a TRANSLATE task apart from a generate one (both
   * reuse the `bulk_generate_*` statuses). `null` on the In progress tabs
   * (the `/products/local` payload does not carry it).
   */
  bulkOperation: ContentMasterBulkReviewItemView.bulkOperation | null;
  language: string | null;
  bulkErrorMessage: string | null;
  source: string | null;
  /**
   * `/bulk/review` payloads: field diffs keyed by storage language code.
   * `null` on the In progress tab.
   */
  reviewLanguages: Record<string, ContentMasterBulkReviewFieldView[]> | null;
}

const ContentMasterBulkReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { bulkGenerationIncluded } = useContentMasterEntitlements();
  // Subuser: reject / mark-for-edit need content_master_write, the bulk apply
  // CTA needs content_master_wawi_write. Retry stays enabled: the backend
  // decides per batch (a 403 there is surfaced by the global interceptor).
  const { can } = usePermissions();
  const canCmWrite = can('content_master_write');
  const canCmWawiWrite = can('content_master_wawi_write');

  const {
    bulkReview,
    workspaceConfig,
    wawiLanguagesError,
    filters: productsFilters,
    menuCounts,
    bulkApplySubmitting,
    bulkRetrySubmitting,
    bulkRetryLoading,
    bulkMarkForEditLoading,
    actionError,
  } = useAppSelector((state) => state.contentMaster);

  /**
   * Cards currently animating out. Keyed by `contentMasterProductId`. The
   * CSS class `.content-master-bulk-card--removing` collapses height +
   * opacity over `SEND_TO_EDITOR_ANIM_MS` ms; once the animation timer
   * fires we dispatch `markForEdit`, which (via the slice reducer) drops
   * the item from `bulkReview.*Items` - React then unmounts the card.
   */
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set<string>());
  const [applyWarningOpen, setApplyWarningOpen] = useState(false);
  /** Active language tab per product on Ready / Errors cards (storage codes from the API). */
  const [activeReviewLangByCmpId, setActiveReviewLangByCmpId] = useState<Record<string, string>>(
    () => ({})
  );
  const [bannerDismissed, setBannerDismissed] = useState(readBulkReviewBannerDismissed);
  /** Pending animation timers - cleared on unmount so we don't leak. */
  const animTimers = useRef<number[]>([]);
  useEffect(() => {
    return () => {
      animTimers.current.forEach((id) => window.clearTimeout(id));
      animTimers.current = [];
    };
  }, []);

  /** Shared HTML preview modal - same UX / DOM as the product edit page. */
  const { open: openHtmlPreview, modal: htmlPreviewModal } = useHtmlPreview();

  const bulkReviewPageRef = useRef<HTMLDivElement | null>(null);
  const bulkReviewToolbarRef = useRef<HTMLDivElement | null>(null);

  /**
   * Precise toolbar height → CSS var for stacked `position: sticky` card
   * headers (avoids overlap / jump when tab row wraps or counts change).
   */
  useLayoutEffect(() => {
    const page = bulkReviewPageRef.current;
    const toolbar = bulkReviewToolbarRef.current;
    if (!page || !toolbar) {
      return;
    }
    const apply = () => {
      const h = Math.ceil(toolbar.getBoundingClientRect().height);
      page.style.setProperty('--cm-bulk-review-sticky-toolbar-h', `${Math.max(h, 1)}px`);
    };
    apply();
    const raf = window.requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(toolbar);
    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const cmReady = workspaceConfig != null || wawiLanguagesError != null;

  /**
   * The channel filter defaults to empty (= all platforms of the data source).
   * Reset it on every entry so the page never carries over a previously picked
   * channel - `source` is optional on the backend list endpoints now.
   */
  useEffect(() => {
    if (bulkReview.filters.source) {
      dispatch(setContentMasterBulkReviewFilters({ source: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** All channels (plattform:N / shop:N) for the channel filter. */
  const channelOptions = useMemo(
    () => buildContentMasterChannelOptions(workspaceConfig?.platforms),
    [workspaceConfig?.platforms]
  );

  /**
   * When set, `GET /bulk/review` is scoped to this language (Products page
   * filter). Omit to load every language the backend stored drafts for.
   */
  const reviewListLanguageFilter = productsFilters.language.trim() || undefined;

  /**
   * Fetch the currently active tab. Each tab has its own backend endpoint:
   *   - generating / applying → /products with the matching bulk_* statuses
   *   - ready                 → /bulk/review with bulk_generate_done
   *   - errors                → /bulk/review with bulk_generate_failed,bulk_apply_failed
   */
  const fetchTab = useCallback(() => {
    if (!cmReady) {
      return;
    }
    const tab = bulkReview.filters.tab;
    if (tab === 'generating' || tab === 'applying') {
      dispatch(
        fetchContentMasterBulkInProgressProducts({
          source: bulkReview.filters.source,
          page: bulkReview.filters.page,
          dataSourceId: workspaceConfig?.dataSourceId ?? null,
          processingStatus: tab === 'generating' ? GENERATING_STATUSES : APPLYING_STATUSES,
        })
      );
      return;
    }
    dispatch(
      fetchContentMasterBulkReviewItems({
        source: bulkReview.filters.source,
        language: reviewListLanguageFilter,
        page: bulkReview.filters.page,
        processingStatus: tab === 'ready' ? READY_STATUS : ERRORS_STATUSES,
        dataSourceId: workspaceConfig?.dataSourceId ?? null,
      })
    );
  }, [
    cmReady,
    bulkReview.filters.source,
    bulkReview.filters.tab,
    bulkReview.filters.page,
    reviewListLanguageFilter,
    workspaceConfig?.dataSourceId,
    dispatch,
  ]);

  useEffect(() => {
    fetchTab();
  }, [fetchTab]);

  // Surface global action errors as toasts (apply / mark-for-edit failures).
  useEffect(() => {
    if (!actionError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: actionError,
      })
    );
  }, [actionError, dispatch, t]);

  const prevBulkReviewPageRef = useRef(bulkReview.filters.page);
  useEffect(() => {
    if (prevBulkReviewPageRef.current !== bulkReview.filters.page) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      prevBulkReviewPageRef.current = bulkReview.filters.page;
    }
  }, [bulkReview.filters.page]);

  const counts = useMemo<Record<ContentMasterBulkReviewTab, number>>(() => {
    const result: Record<ContentMasterBulkReviewTab, number> = {
      generating: 0,
      applying: 0,
      ready: 0,
      errors: 0,
    };
    if (menuCounts) {
      for (const def of TABS) {
        let total = 0;
        for (const s of def.countStatuses) {
          const v = menuCounts[s];
          if (typeof v === 'number') {
            total += v;
          }
        }
        result[def.key] = total;
      }
    }
    return result;
  }, [menuCounts]);

  const handleDismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      localStorage.setItem(BULK_REVIEW_BANNER_DISMISS_KEY, '1');
    } catch {
      // Ignore storage failures - banner stays dismissed for this session.
    }
  }, []);

  const handleSetTab = (next: ContentMasterBulkReviewTab) => {
    if (next === bulkReview.filters.tab) {
      return;
    }
    dispatch(setContentMasterBulkReviewFilters({ tab: next, page: 1 }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    const totalPages = Math.max(1, Math.ceil(bulkReview.total / Math.max(1, bulkReview.limit)));
    if (direction === 'prev' && bulkReview.filters.page > 1) {
      dispatch(setContentMasterBulkReviewFilters({ page: bulkReview.filters.page - 1 }));
      return;
    }
    if (direction === 'next' && bulkReview.filters.page < totalPages) {
      dispatch(setContentMasterBulkReviewFilters({ page: bulkReview.filters.page + 1 }));
    }
  };

  /**
   * Adapter from the two possible source shapes to a single render shape.
   *
   * `mainImageUrl` is read straight from the backend payload on both tabs -
   * `/v2/api/content-master/products` for In progress and
   * `/v2/api/content-master/bulk/review` for Ready / Errors. Both endpoints
   * are self-sufficient for the thumbnail: the bulk-generate worker now
   * persists `main_image_url` on the product row at first generation, so
   * `/bulk/review` returns it directly. `null` simply triggers the
   * letter-avatar fallback inside `ContentMasterProductThumb`.
   */
  const rows: RenderRow[] = useMemo(() => {
    if (bulkReview.filters.tab === 'generating' || bulkReview.filters.tab === 'applying') {
      return bulkReview.inProgressItems.map<RenderRow>((it) => ({
        contentMasterProductId: it.contentMasterProductId ?? null,
        wawiArtikelId: it.wawiArtikelId ?? null,
        sku: it.sku ?? null,
        gtin: it.gtin ?? null,
        title: it.title ?? null,
        thumbUrl: it.mainImageUrl ?? null,
        status: (it.processingStatus as ContentMasterProductView.processingStatus) ?? null,
        bulkOperation: null,
        language: it.language ?? null,
        bulkErrorMessage: it.bulkErrorMessage ?? null,
        source: it.source ?? null,
        reviewLanguages: null,
      }));
    }
    return bulkReview.reviewItems.map<RenderRow>((it: ContentMasterBulkReviewItemView) => ({
      contentMasterProductId: it.contentMasterProductId ?? null,
      wawiArtikelId: it.wawiArtikelId ?? null,
      sku: it.sku ?? null,
      gtin: it.gtin ?? null,
      title: it.titleCache ?? null,
      thumbUrl: it.mainImageUrl ?? null,
      status:
        (it.processingStatus as unknown as ContentMasterProductView.processingStatus) ?? null,
      bulkOperation: it.bulkOperation ?? null,
      language: null,
      bulkErrorMessage: it.bulkErrorMessage ?? null,
      source: null,
      reviewLanguages: reviewLanguagesWithDrafts(it),
    }));
  }, [bulkReview.filters.tab, bulkReview.inProgressItems, bulkReview.reviewItems]);

  /**
   * Prune `removingIds` against the currently rendered `rows`. Without this,
   * a successful Retry leaves the just-retried `cmpId` in the Set forever:
   * the slice drops the row from `reviewItems` (errors tab) and the card
   * unmounts, but the same product reappears under the In Progress tab with
   * `bulk_*_queued` status - and the stale `--removing` class collapses the
   * card to height 0 / opacity 0, so the row is invisible. The prune also
   * keeps the Set bounded over time for the Send-to-Editor flow.
   */
  useEffect(() => {
    setRemovingIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const visible = new Set<string>();
      for (const r of rows) {
        if (r.contentMasterProductId) {
          visible.add(r.contentMasterProductId);
        }
      }
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows]);

  useEffect(() => {
    if (bulkReview.filters.tab === 'generating' || bulkReview.filters.tab === 'applying') {
      return;
    }
    setActiveReviewLangByCmpId((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const row of bulkReview.reviewItems) {
        const cmpId = row.contentMasterProductId;
        if (!cmpId) {
          continue;
        }
        const langs = reviewLanguagesWithDrafts(row);
        const codes = sortStorageLanguageCodes(Object.keys(langs));
        if (codes.length === 0) {
          continue;
        }
        const current = next[cmpId];
        if (!current || !codes.includes(current)) {
          next[cmpId] = codes[0];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [bulkReview.reviewItems, bulkReview.filters.tab]);

  const bulkApplyJobCount = useMemo(() => {
    if (bulkReview.filters.tab !== 'ready') {
      return 0;
    }
    let n = 0;
    for (const row of rows) {
      if (!row.contentMasterProductId || !row.reviewLanguages) {
        continue;
      }
      for (const fields of Object.values(row.reviewLanguages)) {
        if (fields.length > 0) {
          n += 1;
        }
      }
    }
    return n;
  }, [rows, bulkReview.filters.tab]);

  /**
   * Send-to-Editor.
   *
   * Per UX spec we do NOT navigate to the product page and do NOT refetch
   * the list. Sequence:
   *   1. Add the id to `removingIds` - CSS collapses the card (height /
   *      opacity / translateX) over `SEND_TO_EDITOR_ANIM_MS` ms.
   *   2. After the animation timer fires, dispatch
   *      `markContentMasterProductForEdit`. The slice's reducer drops the
   *      item from `bulkReview.reviewItems` / `inProgressItems` and
   *      decrements `bulkReview.total`, so React unmounts the card here.
   *   3. Tab/menu counts refresh via `fetchContentMasterMenuCounts` in
   *      `DashboardLayout` (5s poll while on Content Master routes).
   *   4. On failure: remove the id from `removingIds` - the card snaps
   *      back into place. The global `actionError` toast surfaces the
   *      reason.
   */
  const handleSendToEditor = useCallback(
    (row: RenderRow) => {
      const cmpId = row.contentMasterProductId;
      if (!cmpId) {
        return;
      }
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.add(cmpId);
        return next;
      });
      const timerId = window.setTimeout(async () => {
        try {
          await dispatch(
            markContentMasterProductForEdit({ contentMasterProductId: cmpId })
          ).unwrap();
        } catch {
          // Revert - the slice will not have touched anything (thunk failed).
          setRemovingIds((prev) => {
            if (!prev.has(cmpId)) {
              return prev;
            }
            const next = new Set(prev);
            next.delete(cmpId);
            return next;
          });
        }
      }, SEND_TO_EDITOR_ANIM_MS);
      animTimers.current.push(timerId);
    },
    [dispatch]
  );

  /**
   * `(contentMasterProductId, language)` pairs with stored drafts for one row -
   * each becomes one bulk-apply payload item (the worker applies the latest
   * draft per field for that language).
   */
  const collectRowApplyItems = (
    row: RenderRow
  ): Array<{ contentMasterProductId: string; language: string }> => {
    const items: Array<{ contentMasterProductId: string; language: string }> = [];
    if (!row.contentMasterProductId || !row.reviewLanguages) {
      return items;
    }
    for (const [lang, fields] of Object.entries(row.reviewLanguages)) {
      if (fields.length > 0) {
        items.push({ contentMasterProductId: row.contentMasterProductId, language: lang });
      }
    }
    return items;
  };

  /** Shared submit path for both the footer "apply all" and the per-card Apply. */
  const performApply = useCallback(
    async (items: Array<{ contentMasterProductId: string; language: string }>) => {
      if (items.length === 0) {
        return;
      }
      try {
        const result = await dispatch(
          submitContentMasterBulkApply({
            items,
            dataSourceId: workspaceConfig?.dataSourceId ?? null,
          })
        ).unwrap();
        const acceptedCount = result.accepted?.length ?? 0;
        const skippedCount = result.skipped?.length ?? 0;
        if (acceptedCount === 0 && skippedCount > 0) {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('content_master_bulk_submit_no_accepted'),
            })
          );
        } else {
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: t('content_master_bulk_apply_success_accepted', { count: acceptedCount }),
            })
          );
        }
        fetchTab();
      } catch {
        // Toast handled by global actionError effect above.
      }
    },
    [dispatch, workspaceConfig?.dataSourceId, fetchTab, t]
  );

  /**
   * Apply every product visible on the current Ready page in one call. We
   * intentionally apply only the visible page (per UX decision).
   */
  const performBulkApply = useCallback(async () => {
    await performApply(rows.flatMap(collectRowApplyItems));
  }, [rows, performApply]);

  /**
   * Items awaiting the consent warning: the footer CTA queues the whole page
   * (`null`), a per-card Apply queues that row's items only.
   */
  const [pendingApplyItems, setPendingApplyItems] = useState<
    Array<{ contentMasterProductId: string; language: string }> | null
  >(null);

  /**
   * Footer "Confirm & apply" entry point. Applying pushes drafts to Wawi /
   * the database and may break production listings, so we surface a consent
   * warning first - unless the user previously chose "don't show again"
   * (per-flow key: dismissing here does not affect other apply flows).
   */
  const handleConfirmAndApply = useCallback(() => {
    if (isApplyWarningDismissed('bulk-review')) {
      void performBulkApply();
      return;
    }
    setPendingApplyItems(null);
    setApplyWarningOpen(true);
  }, [performBulkApply]);

  /** Per-card Apply on the Ready tab - same warning gate, one product only. */
  const handleApplyRow = useCallback(
    (row: RenderRow) => {
      const items = collectRowApplyItems(row);
      if (items.length === 0) {
        return;
      }
      if (isApplyWarningDismissed('bulk-review')) {
        void performApply(items);
        return;
      }
      setPendingApplyItems(items);
      setApplyWarningOpen(true);
    },
    [performApply]
  );

  const handleConfirmApplyWarning = useCallback(() => {
    setApplyWarningOpen(false);
    if (pendingApplyItems) {
      void performApply(pendingApplyItems);
    } else {
      void performBulkApply();
    }
    setPendingApplyItems(null);
  }, [pendingApplyItems, performApply, performBulkApply]);

  /**
   * Per-row Retry on the With errors tab.
   *
   * The new `POST /v2/api/content-master/bulk/retry` endpoint reads the
   * original `source` / `language` from the snapshot saved on the product at
   * the time of the original submit (`content_master_product.bulk_config`),
   * so the client only sends `contentMasterProductId`. The server picks the
   * correct worker per item (generate vs. apply) based on the current
   * `processingStatus`.
   *
   * UX: same animation as Send-to-Editor - collapse the card, fire the
   * thunk, and let the slice reducer drop the item from `reviewItems`
   * (accepted rows now have a `bulk_*_queued` status that doesn't match the
   * errors tab whitelist). On failure the card snaps back; the global
   * `actionError` toast surfaces the reason. Quota errors open the shared
   * modal instead - same contract as Bulk Generate.
   */
  const handleRetry = useCallback(
    (row: RenderRow) => {
      const cmpId = row.contentMasterProductId;
      if (!cmpId) {
        return;
      }
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.add(cmpId);
        return next;
      });
      const timerId = window.setTimeout(async () => {
        try {
          const result = await dispatch(
            submitContentMasterBulkRetry({
              items: [{ contentMasterProductId: cmpId }],
              dataSourceId: workspaceConfig?.dataSourceId ?? null,
            })
          ).unwrap();
          const acceptedCount = result.accepted?.length ?? 0;
          const skippedCount = result.skipped?.length ?? 0;
          if (acceptedCount === 0) {
            // Server skipped the only item we sent - restore the card so the
            // user can see it's still in the errors tab and try Send-to-Editor.
            setRemovingIds((prev) => {
              if (!prev.has(cmpId)) {
                return prev;
              }
              const next = new Set(prev);
              next.delete(cmpId);
              return next;
            });
            if (skippedCount > 0) {
              dispatch(
                addNotification({
                  type: 'warning',
                  title: t('notification_warning'),
                  message: t('content_master_bulk_retry_skipped', { count: skippedCount }),
                })
              );
            }
            return;
          }
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: t('content_master_bulk_retry_success', { count: acceptedCount }),
            })
          );
        } catch {
          setRemovingIds((prev) => {
            if (!prev.has(cmpId)) {
              return prev;
            }
            const next = new Set(prev);
            next.delete(cmpId);
            return next;
          });
        }
      }, SEND_TO_EDITOR_ANIM_MS);
      animTimers.current.push(timerId);
    },
    [dispatch, workspaceConfig?.dataSourceId, t]
  );

  /**
   * Per-row Reject on the With errors tab - `POST /products/reject` moves the
   * product to `rejected_by_user` (no un-reject; the next generate/apply
   * submit re-queues it). Same collapse animation as Retry; the slice reducer
   * drops the accepted row from `reviewItems`. On skip/failure the card snaps
   * back.
   */
  const handleReject = useCallback(
    (row: RenderRow) => {
      const cmpId = row.contentMasterProductId;
      if (!cmpId) {
        return;
      }
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.add(cmpId);
        return next;
      });
      const timerId = window.setTimeout(async () => {
        const restore = () =>
          setRemovingIds((prev) => {
            if (!prev.has(cmpId)) {
              return prev;
            }
            const next = new Set(prev);
            next.delete(cmpId);
            return next;
          });
        try {
          const result = await dispatch(
            submitContentMasterProductsReject({
              items: [{ contentMasterProductId: cmpId }],
              dataSourceId: workspaceConfig?.dataSourceId ?? null,
            })
          ).unwrap();
          const acceptedCount = result.accepted?.length ?? 0;
          if (acceptedCount === 0) {
            restore();
            dispatch(
              addNotification({
                type: 'warning',
                title: t('notification_warning'),
                message: t('content_master_reject_skipped', { count: 1 }),
              })
            );
            return;
          }
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: t('content_master_reject_success', { count: acceptedCount }),
            })
          );
        } catch {
          // Toast handled by the global actionError effect above.
          restore();
        }
      }, SEND_TO_EDITOR_ANIM_MS);
      animTimers.current.push(timerId);
    },
    [dispatch, workspaceConfig?.dataSourceId, t]
  );

  /**
   * Footer "Retry all" - submits every visible failed row in one call. The
   * slice removes accepted rows from `reviewItems` and decrements `total`,
   * so the user sees the list shrink (or empty out) as the worker takes over.
   * Skipped rows stay on screen with their existing error message.
   */
  const handleRetryAll = useCallback(async () => {
    const items: Array<{ contentMasterProductId: string }> = [];
    for (const r of rows) {
      if (!r.contentMasterProductId) {
        continue;
      }
      if (
        r.status === ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED &&
        !bulkGenerationIncluded
      ) {
        continue;
      }
      items.push({ contentMasterProductId: r.contentMasterProductId });
    }
    if (items.length === 0) {
      return;
    }
    try {
      const result = await dispatch(
        submitContentMasterBulkRetry({
          items,
          dataSourceId: workspaceConfig?.dataSourceId ?? null,
        })
      ).unwrap();
      const acceptedCount = result.accepted?.length ?? 0;
      const skippedCount = result.skipped?.length ?? 0;
      if (acceptedCount === 0 && skippedCount > 0) {
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: t('content_master_bulk_retry_skipped', { count: skippedCount }),
          })
        );
        return;
      }
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('content_master_bulk_retry_success', { count: acceptedCount }),
        })
      );
    } catch {
      // Toast handled by the global actionError effect above.
    }
  }, [rows, dispatch, workspaceConfig?.dataSourceId, t, bulkGenerationIncluded]);

  /**
   * Footer "Retry all failed" - server-side `all: true` mode: the backend
   * itself collects EVERY failed product of the user for the data source
   * (all pages, bulk AND listing statuses), so nothing is sent per item.
   * Idempotent: with nothing failed the server returns 200 with empty lists.
   * After an accepted retry the tab is refetched - `all` can re-queue rows
   * beyond the current page, which the reducer's local removal can't see.
   */
  const handleRetryAllFailed = useCallback(async () => {
    try {
      const result = await dispatch(
        submitContentMasterBulkRetry({
          all: true,
          dataSourceId: workspaceConfig?.dataSourceId ?? null,
        })
      ).unwrap();
      const acceptedCount = result.accepted?.length ?? 0;
      const skippedCount = result.skipped?.length ?? 0;
      if (acceptedCount === 0 && skippedCount > 0) {
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: t('content_master_bulk_retry_skipped', { count: skippedCount }),
          })
        );
        return;
      }
      if (acceptedCount > 0) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('content_master_bulk_retry_success', { count: acceptedCount }),
          })
        );
        fetchTab();
      }
    } catch {
      // Toast handled by the global actionError effect above.
    }
  }, [dispatch, workspaceConfig?.dataSourceId, t, fetchTab]);

  /**
   * Resolve a human-readable label for one diff field row.
   *   - core fields → i18n key from `CORE_FIELD_LABEL_I18N`
   *   - `wawiAttr` / `wawiMerkmal` → backend-localized `label` when present,
   *     else fall back to `apiName`.
   */
  const resolveFieldLabel = useCallback(
    (field: ContentMasterBulkReviewFieldView): string => {
      const apiName = field.apiName ?? '';
      if (apiName && field.type === 'core') {
        const i18nKey = CORE_FIELD_LABEL_I18N[apiName];
        if (i18nKey) {
          return t(i18nKey);
        }
      }
      if (field.label && field.label.trim()) {
        return field.label.trim();
      }
      return apiName || t('content_master_bulk_review_field_unknown');
    },
    [t]
  );

  const totalPages = Math.max(1, Math.ceil(bulkReview.total / Math.max(1, bulkReview.limit)));
  const startItem = bulkReview.total === 0 ? 0 : (bulkReview.page - 1) * bulkReview.limit + 1;
  const endItem = Math.min(bulkReview.page * bulkReview.limit, bulkReview.total);

  const showEmptyState = !bulkReview.loading && rows.length === 0;
  /**
   * Footer is ALWAYS rendered so the Refresh button stays accessible - even
   * on an empty errors / ready tab where the user may want to poll without a
   * full page reload. Tab-specific CTAs (`showApplyCta`, `showRetryAllCta`)
   * are gated separately and hidden when there are no actionable rows.
   */
  const showApplyCta = bulkReview.filters.tab === 'ready';
  const showRetryAllCta = bulkReview.filters.tab === 'errors';
  const retryableRowCount = useMemo(() => {
    if (bulkReview.filters.tab !== 'errors') {
      return 0;
    }
    let n = 0;
    for (const r of rows) {
      if (!r.contentMasterProductId) {
        continue;
      }
      if (
        r.status === ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED &&
        !bulkGenerationIncluded
      ) {
        continue;
      }
      n += 1;
    }
    return n;
  }, [rows, bulkReview.filters.tab, bulkGenerationIncluded]);
  /**
   * Diffs and per-row Send-to-Editor are scoped to the review tabs only.
   * In Progress rows render a minimal product header, since their drafts may
   * still be mid-flight (the review endpoint refuses these statuses on
   * purpose) and the user has nothing to act on yet.
   */
  const isActiveBulkTab =
    bulkReview.filters.tab === 'generating' || bulkReview.filters.tab === 'applying';
  const showDiffCards = !isActiveBulkTab;
  const showCardActions = !isActiveBulkTab;

  /**
   * Renders the side-by-side Original / Generated diff for one product card.
   * Empty when the backend returned no fields (e.g. a `bulk_generate_failed`
   * row whose AI step crashed before any draft was saved).
   */
  const renderFieldDiffs = (fields: ContentMasterBulkReviewFieldView[]) => {
    if (fields.length === 0) {
      return (
        <div className="content-master-bulk-card__no-fields">
          {t('content_master_bulk_review_card_no_fields')}
        </div>
      );
    }
    return (
      <div className="content-master-bulk-card__fields">
        {fields.map((field, idx) => {
          const label = resolveFieldLabel(field);
          // Show the HTML preview eye-icon for known HTML core fields. The
          // legacy product editor whitelists `shortDescription` /
          // `description` only - we mirror that here. `wawiMerkmal` values
          // are localized labels from the API (see kMerkmalWert* tooltips).
          const isHtmlField =
            field.type === ContentMasterBulkReviewFieldViewNs.type.CORE &&
            !!field.apiName &&
            HTML_PREVIEW_CORE_FIELDS.has(field.apiName);
          const isMerkmal = field.type === ContentMasterBulkReviewFieldViewNs.type.WAWI_MERKMAL;
          // Multi-value Merkmal: prefer the full sorted ASC id list so the
          // tooltip exposes every assigned `kMerkmalWert`, not just the first
          // one. Fall back to the legacy single-id convenience copy when the
          // backend response predates the array fields.
          const buildMerkmalIdsTitle = (
            ids: ReadonlyArray<number> | null | undefined,
            fallbackId: number | null | undefined
          ): string | undefined => {
            if (ids && ids.length > 0) {
              return `kMerkmalWert: ${ids.join(', ')}`;
            }
            if (fallbackId != null) {
              return `kMerkmalWert: ${fallbackId}`;
            }
            return undefined;
          };
          const originalValueTitle = isMerkmal
            ? buildMerkmalIdsTitle(
                field.kMerkmalWerteOriginal,
                field.kMerkmalWertOriginal
              )
            : undefined;
          const generatedValueTitle = isMerkmal
            ? buildMerkmalIdsTitle(
                field.kMerkmalWerteGenerated,
                field.kMerkmalWertGenerated
              )
            : undefined;
          const originalLabel = t('content_master_bulk_review_field_original', { label });
          const generatedLabel = t('content_master_bulk_review_field_generated', { label });
          return (
            <div
              key={field.apiName ?? `field-${idx}`}
              className="content-master-bulk-card__field-row"
            >
              <div className="content-master-bulk-card__field-col">
                <div className="content-master-bulk-card__field-label">
                  <span>{originalLabel}</span>
                  {isHtmlField ? (
                    <button
                      type="button"
                      className="content-master-preview-button"
                      onClick={() => openHtmlPreview(originalLabel, field.original ?? '')}
                      disabled={!field.original}
                      aria-label={t('content_master_preview_html')}
                      title={t('content_master_preview_html')}
                    >
                      <ContentMasterEyeIcon />
                    </button>
                  ) : null}
                </div>
                <div className="content-master-bulk-card__field-value" title={originalValueTitle}>
                  {field.original && field.original.trim()
                    ? field.original
                    : t('content_master_bulk_review_field_empty')}
                </div>
              </div>
              <div className="content-master-bulk-card__field-col">
                <div className="content-master-bulk-card__field-label">
                  <span>{generatedLabel}</span>
                  {isHtmlField ? (
                    <button
                      type="button"
                      className="content-master-preview-button"
                      onClick={() => openHtmlPreview(generatedLabel, field.generated ?? '')}
                      disabled={!field.generated}
                      aria-label={t('content_master_preview_html')}
                      title={t('content_master_preview_html')}
                    >
                      <ContentMasterEyeIcon />
                    </button>
                  ) : null}
                </div>
                <div className="content-master-bulk-card__field-value" title={generatedValueTitle}>
                  {field.generated && field.generated.trim()
                    ? field.generated
                    : t('content_master_bulk_review_field_empty')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderReviewDiffSection = (row: RenderRow) => {
    if (!showDiffCards) {
      return null;
    }
    const langs = row.reviewLanguages;
    if (!langs || Object.keys(langs).length === 0) {
      return renderFieldDiffs([]);
    }
    const codes = sortStorageLanguageCodes(Object.keys(langs));
    const cmpId = row.contentMasterProductId;
    const active = cmpId ? activeReviewLangByCmpId[cmpId] : codes[0];
    const safeActive = codes.includes(active) ? active : codes[0];
    const fields = langs[safeActive] ?? [];
    return (
      <>
        {codes.length > 1 && cmpId ? (
          <div
            className="content-master-bulk-card__lang-tabs"
            role="tablist"
            aria-label={t('content_master_bulk_review_lang_tabs_aria')}
          >
            {codes.map((code) => {
              const isActive = code === safeActive;
              return (
                <button
                  key={code}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={
                    isActive
                      ? 'content-master-bulk-card__lang-tab content-master-bulk-card__lang-tab--active'
                      : 'content-master-bulk-card__lang-tab'
                  }
                  onClick={() =>
                    setActiveReviewLangByCmpId((prev) => ({ ...prev, [cmpId]: code }))
                  }
                >
                  {code}
                </button>
              );
            })}
          </div>
        ) : null}
        {renderFieldDiffs(fields)}
      </>
    );
  };

  /**
   * Compact row used at the top of every product card: ordinal, image+title,
   * SKU, GTIN, status pill. Mirrors the column layout in the screenshot.
   */
  const renderCardHeader = (row: RenderRow, ordinal: number) => (
    <div className="content-master-bulk-card__header">
      <div className="content-master-bulk-card__cell content-master-bulk-card__cell--ordinal">
        <span className="content-master-bulk-card__cell-label">
          {t('content_master_bulk_review_col_ordinal')}
        </span>
        <span className="content-master-bulk-card__cell-value">{ordinal}</span>
      </div>
      <div className="content-master-bulk-card__cell content-master-bulk-card__cell--product">
        <span className="content-master-bulk-card__cell-label">
          {t('content_master_summary_product')}
        </span>
        <div className="content-master-bulk-card__product">
          <ContentMasterProductThumb imageUrl={row.thumbUrl} alt="" />
          <div className="content-master-bulk-card__product-text">
            <div className="content-master-bulk-card__product-title">{row.title || '-'}</div>
            {row.sku && (
              <div className="content-master-bulk-card__product-subtitle">{row.sku}</div>
            )}
          </div>
        </div>
      </div>
      <div className="content-master-bulk-card__cell">
        <span className="content-master-bulk-card__cell-label">SKU</span>
        <span className="content-master-bulk-card__cell-value">{row.sku || '-'}</span>
      </div>
      <div className="content-master-bulk-card__cell">
        <span className="content-master-bulk-card__cell-label">GTIN</span>
        <span className="content-master-bulk-card__cell-value">{row.gtin || '-'}</span>
      </div>
      <div className="content-master-bulk-card__cell">
        <span className="content-master-bulk-card__cell-label">
          {t('content_master_status')}
        </span>
        <span className={getContentMasterStatusClassName(row.status)}>
          {t(contentMasterStatusLabelKey(row.status, row.bulkOperation))}
        </span>
      </div>
    </div>
  );

  return (
    <div ref={bulkReviewPageRef} className="gpsr-content content-master-bulk-review-page">
      {!bannerDismissed ? (
        <div className="content-master-bulk-review-banner" role="note">
          <div className="content-master-bulk-review-banner__icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
          </div>
          <div className="content-master-bulk-review-banner__text">
            <div className="content-master-bulk-review-banner__title">
              {t('content_master_bulk_review_banner_title')}
            </div>
            <div className="content-master-bulk-review-banner__subtitle">
              {t('content_master_bulk_review_banner_subtitle')}
            </div>
          </div>
          <button
            type="button"
            className="content-master-bulk-review-banner__close"
            onClick={handleDismissBanner}
            aria-label={t('content_master_bulk_review_banner_close')}
            title={t('content_master_bulk_review_banner_close')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ) : null}

      <div ref={bulkReviewToolbarRef} className="content-master-bulk-review-toolbar">
        <h1 className="content-master-bulk-review-toolbar__title">
          {t('content_master_bulk_review_page_title')}
        </h1>
        <div
          className="content-master-status-tabs"
          role="tablist"
          aria-label={t('content_master_status_tabs_aria')}
        >
          {TABS.map((def) => {
            const active = bulkReview.filters.tab === def.key;
            return (
              <button
                key={def.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={
                  active
                    ? 'content-master-status-tabs__tab content-master-status-tabs__tab--active'
                    : 'content-master-status-tabs__tab'
                }
                onClick={() => handleSetTab(def.key)}
              >
                <span className="content-master-status-tabs__label">{t(def.labelKey)}</span>
                <span className="content-master-status-tabs__count">{counts[def.key]}</span>
              </button>
            );
          })}
        </div>

        {channelOptions.length > 0 && (
          <div className="content-master-toolbar-selects">
            <div className="content-master-toolbar-select-wrap">
              <select
                className="content-master-toolbar-select"
                value={bulkReview.filters.source}
                onChange={(event) =>
                  dispatch(
                    setContentMasterBulkReviewFilters({ source: event.target.value, page: 1 })
                  )
                }
                aria-label={t('content_master_bulk_review_channel_label')}
              >
                <option value="">{t('content_master_bulk_review_channel_all')}</option>
                {channelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {showEmptyState ? (
        <EmptyState
          title={t('content_master_bulk_review_empty_title')}
          description={t('content_master_bulk_review_empty_description')}
        />
      ) : (
        <div className="content-master-bulk-review-list" aria-busy={bulkReview.loading}>
          {bulkReview.loading && (
            <div
              className="content-master-table-panel__overlay"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="order-sidebar-spinner" aria-label={t('loading_text')} />
            </div>
          )}
          {rows.map((row, idx) => {
            const cmId = row.contentMasterProductId ?? '';
            const editLoading = cmId ? bulkMarkForEditLoading[cmId] === true : false;
            const ordinal = (bulkReview.page - 1) * bulkReview.limit + idx + 1;
            const isRemoving = !!cmId && removingIds.has(cmId);
            // Active-tab card with no cached title/image yet = the product has
            // not been fetched from Wawi into our DB - show a shimmering
            // "being prepared" placeholder instead of an empty card.
            const pending =
              (bulkReview.filters.tab === 'generating' ||
                bulkReview.filters.tab === 'applying') &&
              !row.title?.trim() &&
              !row.thumbUrl?.trim();
            if (pending) {
              return (
                <div
                  key={cmId || `${row.wawiArtikelId}-${idx}`}
                  className="content-master-bulk-card content-master-bulk-card--pending"
                >
                  <span className="content-master-row-pending__shimmer" aria-hidden />
                  <span className="content-master-row-pending__text">
                    {t('content_master_product_pending')}
                  </span>
                </div>
              );
            }
            const cardClass = isRemoving
              ? 'content-master-bulk-card content-master-bulk-card--removing'
              : 'content-master-bulk-card';
            return (
              <div key={cmId || `${row.wawiArtikelId}-${row.sku}`} className={cardClass}>
                {renderCardHeader(row, ordinal)}

                {renderReviewDiffSection(row)}

                {row.bulkErrorMessage && bulkReview.filters.tab === 'errors' && (
                  <div
                    className="content-master-bulk-card__error"
                    title={row.bulkErrorMessage}
                  >
                    <strong>{t('content_master_bulk_review_col_error')}:</strong>{' '}
                    {row.bulkErrorMessage}
                  </div>
                )}

                {showCardActions && (
                  <div className="content-master-bulk-card__actions">
                    {bulkReview.filters.tab === 'errors' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary content-master-bulk-card__retry"
                          onClick={() => {
                            if (
                              row.status ===
                                ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED &&
                              !bulkGenerationIncluded
                            ) {
                              dispatch(
                                setContentMasterSubscriptionInsufficientModalVisible(true)
                              );
                              return;
                            }
                            handleRetry(row);
                          }}
                          disabled={
                            !row.contentMasterProductId ||
                            (cmId ? bulkRetryLoading[cmId] === true : false) ||
                            bulkRetrySubmitting ||
                            editLoading ||
                            (row.status ===
                              ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED &&
                              !bulkGenerationIncluded)
                          }
                          title={
                            row.status ===
                              ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED &&
                            !bulkGenerationIncluded
                              ? t('cm_entitlements_starter_bulk_disabled')
                              : undefined
                          }
                        >
                          {t('content_master_bulk_review_retry')}
                        </button>
                        {row.status ===
                          ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED &&
                        !bulkGenerationIncluded ? (
                          <ContentMasterProBadge
                            className="content-master-bulk-card__pro-badge"
                            title={t('cm_entitlements_starter_bulk_disabled')}
                          />
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-secondary content-master-bulk-card__reject"
                          onClick={() => handleReject(row)}
                          disabled={
                            !row.contentMasterProductId ||
                            (cmId ? bulkRetryLoading[cmId] === true : false) ||
                            bulkRetrySubmitting ||
                            editLoading ||
                            !canCmWrite
                          }
                          title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                        >
                          {t('content_master_bulk_review_reject')}
                        </button>
                      </>
                    )}
                    {bulkReview.filters.tab === 'ready' && (
                      <button
                        type="button"
                        className="btn btn-primary content-master-bulk-card__apply"
                        onClick={() => handleApplyRow(row)}
                        disabled={
                          bulkApplySubmitting ||
                          editLoading ||
                          !row.contentMasterProductId ||
                          !canCmWawiWrite
                        }
                        title={
                          !canCmWawiWrite ? t('subuser_no_permission_tooltip') : undefined
                        }
                      >
                        {t('content_master_bulk_review_apply_one')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary content-master-bulk-card__send-to-editor"
                      onClick={() => handleSendToEditor(row)}
                      disabled={
                        editLoading ||
                        !row.contentMasterProductId ||
                        (cmId ? bulkRetryLoading[cmId] === true : false) ||
                        !canCmWrite
                      }
                      title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                    >
                      {t('content_master_bulk_review_send_to_editor')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {rows.length > 0 && (
        <Pagination
          startItem={startItem}
          endItem={endItem}
          total={bulkReview.total}
          totalPages={totalPages}
          page={bulkReview.page}
          loading={bulkReview.loading}
          handlePageChange={handlePageChange}
          onGotoPage={(targetPage) =>
            dispatch(setContentMasterBulkReviewFilters({ page: targetPage }))
          }
        />
      )}

      <ContentMasterReviewFooter
        onRefresh={fetchTab}
        refreshing={bulkReview.loading}
        primaryLabel={
          showApplyCta
            ? t('content_master_bulk_review_footer_apply', { count: bulkApplyJobCount })
            : showRetryAllCta
            ? t('content_master_bulk_review_footer_retry_all', { count: retryableRowCount })
            : undefined
        }
        onPrimary={
          showApplyCta ? handleConfirmAndApply : showRetryAllCta ? handleRetryAll : undefined
        }
        primaryDisabled={
          showApplyCta
            ? bulkApplySubmitting || bulkApplyJobCount === 0 || !canCmWawiWrite
            : showRetryAllCta
            ? bulkRetrySubmitting || retryableRowCount === 0
            : false
        }
        primaryTitle={showApplyCta && !canCmWawiWrite ? t('subuser_no_permission_tooltip') : undefined}
        secondaryLabel={
          showRetryAllCta ? t('content_master_bulk_review_footer_retry_all_failed') : undefined
        }
        onSecondary={showRetryAllCta ? handleRetryAllFailed : undefined}
        secondaryDisabled={bulkRetrySubmitting}
      />

      <ContentMasterQuotaExceededModal />

      <ContentMasterApplyWarningModal
        isOpen={applyWarningOpen}
        onClose={() => {
          setApplyWarningOpen(false);
          setPendingApplyItems(null);
        }}
        flow="bulk-review"
        onConfirm={handleConfirmApplyWarning}
        isSubmitting={bulkApplySubmitting}
      />

      {htmlPreviewModal}
    </div>
  );
};

export default ContentMasterBulkReviewPage;
